import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { isArray, isEqual, isEmpty } from 'lodash';
import momentTz from 'moment-timezone';
import { Observable } from 'rxjs';
import urljoin from 'url-join';
import i18n from '../../../i18n/i18n';
import { PTVNErrorAPI } from '../../api/response/BaseResponse';
import { PtvnSeller } from '../../api/response/EMarketplaceResponse';
import { LangType } from '../../graphql/autogenerate/schemas';
import { Country } from '../../models/Address';
import { MarketplacePageRoute } from '../../pages/Marketplace/MarketplacePageRoute';
import { MarketplaceSearchQuery } from '../../pages/Marketplace/MarketplaceSearch/SearchQueryModel';
import { DateTimeFormat } from '../constants/CommonConstants';
import { CountryListConst as CountryConst } from '../constants/CountryConstant';
import PageRoute from '../constants/PageRoute';
import { CountryListConstMap } from './../constants/CountryConstant';
import { isNil, isNull } from 'lodash';
import { UserRoleType } from '../constants/JobBoardConstants';
import { SubscriptionPlanEnum } from '../../models/SubscriptionPlan';

type BreadCrumpObject = { title: string; to?: string | undefined };

export class CommonHelper {
  public static objectToFormData(obj: any, rootName: string = ''): FormData {
    const formData = new FormData();

    function appendFormData(data: any, root: string) {
      root = root || '';
      if (data instanceof File || data instanceof Blob) {
        formData.append(root, data);
      } else if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
          appendFormData(data[i], root + '[' + i + ']');
        }
      } else if (typeof data === 'object' && data) {
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            if (root === '') {
              appendFormData(data[key], key);
            } else {
              appendFormData(data[key], root + '.' + key);
            }
          }
        }
      } else {
        if (data !== null && typeof data !== 'undefined') {
          formData.append(root, data);
        }
      }
    }

    appendFormData(obj, rootName);
    return formData;
  }

  public static composeDateTimeToTimestamp(
    date: string,
    time: string,
    timeZoneID: string,
    dateFormat: string = DateTimeFormat.DDMMYYYY_HYPHEN,
    timeFormat: string = DateTimeFormat.HHmm,
  ): number {
    return +momentTz.tz(`${date} ${time}`, `${dateFormat} ${timeFormat}`, timeZoneID).format('x');
  }

  public static onDisplayComponentByRole(
    role: UserRoleType | undefined,
    componentCondition: any,
    component: any,
  ): any {
    if (!role) {
      return;
    }

    return role === UserRoleType.SUPPLIER ? componentCondition : component;
  }

  public static isValidPhoneNumber(phone: string): boolean {
    const phoneNumberUtil = PhoneNumberUtil.getInstance();
    const phoneNumber = phoneNumberUtil.parse(phone);
    if (phoneNumber) {
      return phoneNumberUtil.isValidNumber(phoneNumber);
    }
    return false;
  }

  public static objectToQueryString(obj: any, notFilter = false): string {
    const query = Object.keys(obj)
      .filter(
        (key) => (obj[key] !== '' && obj[key] !== null && obj[key] !== undefined) || notFilter,
      )
      .map((key) => `${key}=${obj[key]}`)
      .join('&');
    return query.length > 0 ? `?${query}` : '';
  }

  public static getUnixFormTimestamp(timeStamp: number): number {
    return timeStamp / 1000;
  }

  public static displayDate(timeStamp: number, format: string): string {
    return dayjs.unix(CommonHelper.getUnixFormTimestamp(timeStamp)).format(format);
  }

  public static getDefaultCountryPhoneInput(): Country[] {
    const country = [...CountryConst];
    return country.map((c) => ({
      countryCode: c.alpha2Code,
      titleEN: c.name,
      countryCallingCode: `+${c.callingCodes[0]}`,
    }));
  }

  public static navigateToUrl(url: string): void {
    window.location.href = url;
  }

  public static numberWithCommas(data: number) {
    let numbers = data.toString().split('.');
    numbers[0] = numbers[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return numbers.join('.');
  }

  public static businessTypeDisplay = (data: string[], t: Function) => {
    if (!data || data.length === 0) {
      return '-';
    }
    return data.map((item) => t('SearchPage.BusinessType.' + item)).join(', ');
  };

  public static yearOfSell = (data: PtvnSeller, t: Function) => {
    if (!data) {
      return '';
    }
    let template = '';
    if (data.years === 0) {
      return t('SearchPage.Label.LessThan1YearJoined');
    }

    if (data.years) {
      template += `${t('Common.Label.YearLowerCount', { count: data.years })} `;
    }

    return t('Common.Label.PTVNSellerJoined', { msg: template });
  };

  public static displayData(data: string) {
    return data ? data : '-';
  }

  public static getLangKey<
    LocalKey extends string | number | symbol,
    ENKey extends string | number | symbol,
  >(localObjKey: LocalKey, ENObjKey: ENKey, countryCode?: string) {
    const currLang = i18n.language.toLowerCase();
    const enSelected = { selectedKey: ENObjKey, unselectedKey: localObjKey };
    const localSelected = { selectedKey: localObjKey, unselectedKey: ENObjKey };

    if (currLang.startsWith(LangType.En.toLowerCase())) {
      return enSelected;
    }

    return countryCode &&
      CountryListConstMap[countryCode] &&
      CountryListConstMap[countryCode].languages.some((lang) => currLang.includes(lang))
      ? localSelected
      : enSelected;
  }

  public static getLang<ObjLang, LocalKey extends keyof ObjLang, ENKey extends keyof ObjLang>(
    objLang: ObjLang,
    localObjKey: LocalKey,
    ENObjKey: ENKey,
    countryCode?: string,
  ) {
    const langKey = CommonHelper.getLangKey(localObjKey, ENObjKey, countryCode);
    return objLang[langKey.selectedKey]
      ? objLang[langKey.selectedKey]
      : objLang[langKey.unselectedKey];
  }

  public static fromEventSource<T>(url: string) {
    return new Observable<T>((observer) => {
      const source = new EventSource(url);

      function onError(this: EventSource, ev: EventSourceEventMap['error']) {
        if (this.readyState === EventSource.CLOSED) {
          observer.complete();
        } else {
          observer.error(ev);
        }
      }

      function onMessage(this: EventSource, event: EventSourceEventMap['message']) {
        observer.next(JSON.parse(event.data));
      }

      source.addEventListener<'error'>('error', onError, false);
      source.addEventListener<'message'>('message', onMessage, false);

      return () => {
        source.removeEventListener('error', onError, false);
        source.removeEventListener('message', onMessage, false);
        source.close();
      };
    });
  }

  public static getUserClientTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public static getCurrentDateInUserTimeZone() {
    return momentTz().tz(this.getUserClientTimeZone());
  }

  public static uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  public static setDefaultTab = (url: string, defaultTab: string, tabs: any[]) => {
    const params = new URLSearchParams(url);
    const tab = params.get('tab');
    if (tab) {
      const foundTab = tabs.find((value) => value.type === tab);
      return (foundTab && foundTab.type) || defaultTab;
    }
    return defaultTab;
  };

  public static countDecimals = (val: number): number => {
    if (Math.floor(val) === val) return 0;
    const str = val?.toString();
    if (str) {
      if (str.indexOf('.') !== -1 && str.indexOf('-') !== -1) {
        return +str.split('-')[1] || 0;
      } else if (str.indexOf('.') !== -1) {
        return str.split('.')[1].length || 0;
      }
      return +str.split('-')[1] || 0;
    }
    return 0;
  };

  public static scaleDecimalFractionDigit = (
    minimumFractionDigit: number,
    maximumFractionDigit: number,
    val: number,
  ): number => {
    const decimalPlaceCount = +CommonHelper.countDecimals(val);
    if (decimalPlaceCount <= minimumFractionDigit) {
      return minimumFractionDigit;
    } else if (decimalPlaceCount > maximumFractionDigit) {
      return maximumFractionDigit;
    }
    return decimalPlaceCount;
  };

  public static roleSwitchCase<T>(
    role: 'buyer' | 'supplier' | 'admin',
    buyerCase?: () => T,
    supplierCase?: () => T,
    adminCase?: () => T,
  ) {
    switch (role) {
      case 'buyer':
        return buyerCase ? buyerCase() : undefined;
      case 'supplier':
        return supplierCase ? supplierCase() : undefined;
      case 'admin':
        return adminCase ? adminCase() : undefined;
    }
  }

  public static getDefaultBreadCrumpList(
    query: MarketplaceSearchQuery,
    location: any,
  ): BreadCrumpObject[] {
    return [
      {
        title: 'SearchPage.Label.MainPage',
        to: urljoin(PageRoute.MARKETPLACE, MarketplacePageRoute.MAIN_PAGE),
      },
      {
        title: query.keyword ? query.keyword : 'SearchPage.Label.AllSearchResults',
        to: `${location.pathname}${location.search}`,
      },
    ];
  }

  public static getParamsEncodeBreadCrumpParams(
    existBreadCrump: BreadCrumpObject[],
    breadCrump?: BreadCrumpObject,
  ): string {
    const breadCrumpList = [...existBreadCrump];
    breadCrump && breadCrumpList.push(breadCrump);
    return (
      '?title=' +
      encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(breadCrumpList)))))
    );
  }

  public static decodeBreadCrumpParams(searchPath: string): BreadCrumpObject[] {
    const params = Object.fromEntries(new URLSearchParams(searchPath));
    const decodeData = decodeURIComponent(escape(atob(decodeURIComponent(params.title))));
    return JSON.parse(decodeData);
  }

  public static translateBreadCrump(
    t: Function,
    breadCrump: BreadCrumpObject[],
  ): BreadCrumpObject[] {
    return breadCrump.map((data: BreadCrumpObject) => {
      data.title = t(data.title);
      return data;
    });
  }

  public static splitFileName = (originalFileName: string) => {
    const indexOfDot = originalFileName.lastIndexOf('.');
    const fileName = originalFileName.substring(0, indexOfDot);
    const extension = originalFileName.substring(indexOfDot + 1);

    return {
      fileName,
      extension,
    };
  };

  public static composeDateTime = (
    date?: string,
    dateFormat?: string,
    time?: string,
    dateTimeFormat?: string,
  ): momentTz.Moment | null => {
    return isNil(date)
      ? null
      : isNil(time)
      ? momentTz(date, dateFormat)
      : momentTz(`${date} ${time}`, dateTimeFormat);
  };

  public static getDateTimeCompose = (
    date?: string,
    dateFormat?: string,
    time?: string,
    dateTimeFormat?: string,
  ) => {
    return CommonHelper.composeDateTime(date, dateFormat, time, dateTimeFormat);
  };

  public static isDateBefore = (date1: momentTz.Moment | null, date2: momentTz.Moment | null) => {
    return !(isNull(date1) || isNull(date2)) && date1.isBefore(date2);
  };

  public static isDateBeforeNow = (date: momentTz.Moment | null) => {
    return !isNull(date) && date.isBefore(momentTz());
  };

  public static isSameDate = (
    date1: momentTz.Moment | null,
    date2: momentTz.Moment | null,
  ): boolean => {
    return (
      !(isNull(date1) || isNull(date2)) &&
      date1.clone()?.startOf('day').isSame(date2.clone()?.startOf('day'))
    );
  };

  public static isDateBeforeOrSame = (
    date1: momentTz.Moment | null,
    date2: momentTz.Moment | null,
  ) => {
    return !(isNull(date1) || isNull(date2)) && date1.isSameOrBefore(date2);
  };

  public static getPTVNErrorResponseErrorCode(
    errorResponse: AxiosResponse<any>,
    errorCode: string,
  ) {
    if (isArray(errorResponse.data.errors)) {
      const errors = errorResponse.data.errors as PTVNErrorAPI[];
      return errors.find((item) => item.code === errorCode);
    }
    return undefined;
  }

  public static dateTimeToFormat(date: Date, format: string): string {
    return momentTz(date).format(format);
  }

  public static composeMomentToTimestamp(momentDateTime: momentTz.Moment): number {
    const dateFormat = DateTimeFormat.DDMMYYYY_SLASH;
    const timeFormat = DateTimeFormat.HHmm;
    const formattedDate = momentDateTime.format(dateFormat);
    const formattedTime = momentDateTime.format(timeFormat);
    return this.composeDateTimeToTimestamp(
      formattedDate,
      formattedTime,
      CommonHelper.getUserClientTimeZone(),
      dateFormat,
      timeFormat,
    );
  }

  public static scrollToError(ref: HTMLElement | null | undefined) {
    if (!ref) return;
    var isInvalidElement: NodeListOf<any> = ref.querySelectorAll('.is-invalid');
    if (!isEmpty(isInvalidElement)) {
      isInvalidElement[0] &&
        isInvalidElement[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
      isInvalidElement[0] && isInvalidElement[0].focus();
    } else {
      var invalidFeedback: NodeListOf<any> = ref.querySelectorAll(
        '.invalid-feedback,.font-small.text-danger,.color-danger',
      );
      invalidFeedback[0] &&
        invalidFeedback[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  public static pushEachIfAbsent<T>(arr: T[], newArr: T[]): T[] {
    const checkedAbsents: T[] = [];
    for (let item of newArr) {
      let absent = true;
      for (let existItem of arr) {
        if (isEqual(existItem, item)) {
          absent = false;
          break;
        }
      }

      absent && checkedAbsents.push(item);
    }

    return [...arr, ...checkedAbsents];
  }

  public static sortNumberStringList(arr: string[], dir: 'asc' | 'desc'): string[] {
    try {
      const casted: number[] = arr.map((a) => +a);

      return (dir === 'asc' ? casted.sort() : casted.reverse()).map((c) => c.toString());
    } catch (e) {
      throw e;
    }
  }

  public static interfaceToEnum<T>() {
    return new Proxy(
      {},
      {
        get: function (_target, prop, _receiver) {
          return prop;
        },
      },
    ) as {
      [P in keyof T]-?: P;
    };
  }

  public static getPackageName(packageName: string): string {
    switch (packageName) {
      case SubscriptionPlanEnum.PREMIUM:
        return 'Premium';
      case SubscriptionPlanEnum.PREMIUM_PRO:
        return 'Premium<small class="d-inline ml-4px">- Pro</small>';
      case SubscriptionPlanEnum.GOLD:
        return 'Gold';
      case SubscriptionPlanEnum.SILVER:
        return 'Sliver';
      case SubscriptionPlanEnum.DIAMOND:
        return 'Diamond';
      case SubscriptionPlanEnum.PLATINUM:
        return 'Platinum';
      default:
        return 'Free';
    }
  }
}
